import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImageUpload } from "@/components/shared/ImageUpload";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Pencil, X, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminDeals() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDeal, setEditingDeal] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", image_url: "", active: true, order: 0 });

  const fetchDeals = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/deals?active_only=false`, { headers: { Authorization: `Bearer ${token}` } });
      setDeals(res.data);
    } catch (err) { toast.error("Failed to load deals"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDeals(); }, []);

  const resetForm = () => {
    setForm({ title: "", description: "", image_url: "", active: true, order: deals.length });
    setEditingDeal(null);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    const token = localStorage.getItem("token");
    try {
      if (editingDeal) {
        await axios.put(`${API_URL}/deals/${editingDeal}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Deal updated");
      } else {
        await axios.post(`${API_URL}/deals`, { ...form, order: deals.length }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Deal created");
      }
      resetForm();
      fetchDeals();
    } catch (err) { toast.error("Failed to save deal"); }
  };

  const handleEdit = (deal) => {
    setEditingDeal(deal.id);
    setForm({ title: deal.title, description: deal.description, image_url: deal.image_url, active: deal.active, order: deal.order });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this deal?")) return;
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API_URL}/deals/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deal deleted");
      fetchDeals();
    } catch (err) { toast.error("Failed to delete deal"); }
  };

  const handleToggleActive = async (deal) => {
    const token = localStorage.getItem("token");
    try {
      await axios.put(`${API_URL}/deals/${deal.id}`, { ...deal, active: !deal.active }, { headers: { Authorization: `Bearer ${token}` } });
      fetchDeals();
    } catch (err) { toast.error("Failed to update deal"); }
  };

  const handleMove = async (index, direction) => {
    const swapIdx = direction === "up" ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= deals.length) return;
    const token = localStorage.getItem("token");
    try {
      await Promise.all([
        axios.put(`${API_URL}/deals/${deals[index].id}`, { ...deals[index], order: deals[swapIdx].order }, { headers: { Authorization: `Bearer ${token}` } }),
        axios.put(`${API_URL}/deals/${deals[swapIdx].id}`, { ...deals[swapIdx], order: deals[index].order }, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      fetchDeals();
    } catch (err) { toast.error("Failed to reorder"); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-deals-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading font-bold text-2xl text-navy" data-testid="admin-deals-title">In-Store Deals</h1>
            <p className="text-slate-500 text-sm mt-1">Manage deals displayed on all location pages</p>
          </div>
        </div>

        {/* Deal Form */}
        <Card className="border-slate-200" data-testid="deal-form-card">
          <CardContent className="p-6">
            <h2 className="font-heading font-semibold text-navy text-lg mb-4">
              {editingDeal ? "Edit Deal" : "Add New Deal"}
            </h2>
            <div className="grid gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Title *</label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Get a FREE Samsung S25 FE"
                  data-testid="deal-title-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="e.g. Simply switch to the Total 5G or 5G+ Unlimited 3-month plan."
                  className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal"
                  data-testid="deal-description-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Deal Image</label>
                <ImageUpload
                  value={form.image_url}
                  onChange={(url) => setForm({ ...form, image_url: url })}
                  label="Deal Image"
                  folder="deals"
                  testId="deal-image-upload"
                />
              </div>
              <div className="flex items-center gap-4">
                <Button onClick={handleSave} className="bg-teal hover:bg-teal/90 text-white" data-testid="deal-save-btn">
                  {editingDeal ? "Update Deal" : "Add Deal"}
                </Button>
                {editingDeal && (
                  <Button variant="outline" onClick={resetForm} data-testid="deal-cancel-btn">
                    <X className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Deals List */}
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h2 className="font-heading font-semibold text-navy text-lg mb-4">
              Current Deals ({deals.length})
            </h2>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal mx-auto"></div>
              </div>
            ) : deals.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No deals yet. Add your first deal above.</p>
            ) : (
              <div className="space-y-3">
                {deals.map((deal, idx) => (
                  <div
                    key={deal.id}
                    className={`flex items-center gap-4 p-4 rounded-lg border ${deal.active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-60"}`}
                    data-testid={`deal-item-${deal.id}`}
                  >
                    <div className="flex flex-col gap-1">
                      <button onClick={() => handleMove(idx, "up")} disabled={idx === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-30" data-testid={`deal-move-up-${deal.id}`}>
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleMove(idx, "down")} disabled={idx === deals.length - 1} className="text-slate-400 hover:text-slate-600 disabled:opacity-30" data-testid={`deal-move-down-${deal.id}`}>
                        <ArrowDown className="w-4 h-4" />
                      </button>
                    </div>
                    {deal.image_url && (
                      <img src={deal.image_url} alt={deal.title} className="w-16 h-16 object-cover rounded-lg border flex-shrink-0" />
                    )}
                    {!deal.image_url && (
                      <div className="w-16 h-16 bg-slate-100 rounded-lg border flex items-center justify-center flex-shrink-0">
                        <GripVertical className="w-5 h-5 text-slate-300" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-navy text-sm truncate">{deal.title}</h3>
                      {deal.description && <p className="text-slate-500 text-xs mt-0.5 line-clamp-2">{deal.description}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => handleToggleActive(deal)} className={`p-1.5 rounded ${deal.active ? "text-green-600 hover:bg-green-50" : "text-slate-400 hover:bg-slate-100"}`} title={deal.active ? "Active" : "Inactive"} data-testid={`deal-toggle-${deal.id}`}>
                        {deal.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleEdit(deal)} className="p-1.5 rounded text-slate-500 hover:bg-slate-100" data-testid={`deal-edit-${deal.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(deal.id)} className="p-1.5 rounded text-red-500 hover:bg-red-50" data-testid={`deal-delete-${deal.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
